import { Decorative } from "@faire/design-tokens";
import { NonBreaking } from "@faire/web--source/common/string";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { useShowEligibleInsiderMessaging } from "@faire/retailer/hooks/insider/useShowEligibleInsiderMessaging";

interface IInsiderBadgeProps {
  showShortText?: boolean;
  showFreeDuties?: boolean;
  showFreeShipping?: boolean;
  className?: string;
  small?: boolean;
  insiderBadgeText?: string;
  shouldUseFallbackText?: boolean;
  isInsiderMarketingPage?: boolean;
  viewPeteFunction?: (content?: string) => void;
}

export const InsiderBadge: React.FC<IInsiderBadgeProps> = ({
  className,
  small,
  showShortText,
  showFreeDuties,
  showFreeShipping,
  insiderBadgeText,
  shouldUseFallbackText,
  isInsiderMarketingPage,
  viewPeteFunction,
}) => {
  const shouldUseInsiderBadgeText = !shouldUseFallbackText;

  return (
    <StyledTypography
      className={className}
      variant={
        small
          ? {
              mobileAndAbove: "labelSansMedium",
              desktopAndAbove: "paragraphSansMedium",
            }
          : "paragraphSansMedium"
      }
    >
      <TextWrapper>
        <BadgeText
          showShortText={showShortText}
          showFreeDuties={showFreeDuties}
          showFreeShipping={showFreeShipping}
          insiderBadgeText={
            shouldUseInsiderBadgeText ? insiderBadgeText : undefined
          }
          isInsiderMarketingPage={isInsiderMarketingPage}
          viewPeteFunction={viewPeteFunction}
        />
      </TextWrapper>
      &nbsp;
      <InlineInsider
        small
        color={Decorative.icon.teal}
        fill={Decorative.icon.teal}
        fontSize="16px"
      />
    </StyledTypography>
  );
};

const InsiderBadgeText: React.FC<Partial<IInsiderBadgeProps>> = ({
  insiderBadgeText,
  viewPeteFunction,
}) => {
  const onVisibleRef = useDoActionWhenVisible(() => {
    viewPeteFunction?.(insiderBadgeText);
  });

  return <span ref={onVisibleRef}>{insiderBadgeText}</span>;
};

interface IBadgeTextProps {
  showShortText?: boolean;
  showFreeDuties?: boolean;
  showFreeShipping?: boolean;
  insiderBadgeText?: string;
  isInsiderMarketingPage?: boolean;
  viewPeteFunction?: (content?: string) => void;
}

const BadgeText: React.FC<IBadgeTextProps> = ({
  showShortText,
  showFreeDuties,
  showFreeShipping,
  insiderBadgeText,
  isInsiderMarketingPage,
  viewPeteFunction,
}) => {
  const { showEligibleInsiderMessaging } = useShowEligibleInsiderMessaging();

  if (insiderBadgeText) {
    return (
      <InsiderBadgeText
        insiderBadgeText={insiderBadgeText}
        viewPeteFunction={viewPeteFunction}
      />
    );
  }

  if (showEligibleInsiderMessaging || isInsiderMarketingPage) {
    return (
      <BadgeNUXText
        showShortText={showShortText}
        showFreeDuties={showFreeDuties}
        showFreeShipping={showFreeShipping}
      />
    );
  }

  if (showShortText) {
    return <>Insider</>;
  }

  if (showFreeDuties && showFreeShipping) {
    return (
      <StrictLocalMsg
        defaultMessage="Insider{nbsp}free{nbsp}shipping &{nbsp}duties"
        description={{
          text: "Tile badge showing the Insider benefits - free shipping & duties on select brands.",
          img: "https://cdn.faire.com/fastly/c3a3b002e958b15b85a75869cc81b202915a7132abf32f97d5144b397ee1d22e.png",
        }}
        values={{
          nbsp: NonBreaking.space,
        }}
      />
    );
  }

  if (showFreeShipping) {
    return (
      <StrictLocalMsg
        defaultMessage="Insider free{nbsp}shipping"
        description={{
          text: "Tile badge showing the Insider benefits - free shipping & duties on select brands.",
          img: "https://cdn.faire.com/fastly/c3a3b002e958b15b85a75869cc81b202915a7132abf32f97d5144b397ee1d22e.png",
        }}
        values={{
          nbsp: NonBreaking.space,
        }}
      />
    );
  }

  if (showFreeDuties) {
    return (
      <StrictLocalMsg
        defaultMessage="Insider free{nbsp}duties"
        description={{
          text: "Tile badge showing the Insider benefits - free shipping & duties on select brands.",
          img: "https://cdn.faire.com/fastly/c3a3b002e958b15b85a75869cc81b202915a7132abf32f97d5144b397ee1d22e.png",
        }}
        values={{
          nbsp: NonBreaking.space,
        }}
      />
    );
  }

  return <>Insider</>;
};

const BadgeNUXText: React.FC<IBadgeTextProps> = ({
  showShortText,
  showFreeDuties,
  showFreeShipping,
}) => {
  if (showFreeDuties && showFreeShipping) {
    return showShortText ? (
      <StrictLocalMsg
        defaultMessage="Free{nbsp}shipping &{nbsp}duties"
        description={{
          text: "Tile badge showing the Insider benefits - free shipping & duties on select brands.",
          img: "https://cdn.faire.com/fastly/7dcc5eb39ebba5a660b97231df28042ba871c81ad310e20c8994d5903dd846b6.png",
        }}
        values={{
          nbsp: NonBreaking.space,
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Free{nbsp}shipping &{nbsp}duties with Insider"
        description={{
          text: "Tile badge showing the Insider benefits - free shipping & duties on select brands.",
          img: "https://cdn.faire.com/fastly/da65ec74a8593b003984ecd0efc29a5f659ee6ca88dc838cf4f0685af9303028.png",
        }}
        values={{
          nbsp: NonBreaking.space,
        }}
      />
    );
  }

  if (showFreeShipping) {
    return showShortText ? (
      <StrictLocalMsg
        defaultMessage="Free{nbsp}shipping"
        description={{
          text: "Tile badge showing the Insider benefits - free shipping on select brands.",
          img: "https://cdn.faire.com/fastly/26414d83e84deff71efc8f793018e0a3fc9b6c713b2c2b39f31080acb19a2a95.png",
        }}
        values={{
          nbsp: NonBreaking.space,
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Free{nbsp}shipping with Insider"
        description={{
          text: "Tile badge showing the Insider benefits - free shipping on select brands.",
          img: "https://cdn.faire.com/fastly/75c124598cb4522b1cf868135fec0ec9bf6523eff74196810ab0b282d95b37c6.png",
        }}
        values={{
          nbsp: NonBreaking.space,
        }}
      />
    );
  }

  if (showFreeDuties) {
    return showShortText ? (
      <StrictLocalMsg
        defaultMessage="Free{nbsp}duties"
        description={{
          text: "Tile badge showing the Insider benefits - free duties on select brands.",
          img: "https://cdn.faire.com/fastly/5ab73b9c730d1bf5385ca04340ee87120061b8c5fcd7d1de003fedb84140a77f.png",
        }}
        values={{
          nbsp: NonBreaking.space,
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Free{nbsp}duties with Insider"
        description={{
          text: "Tile badge showing the Insider benefits - free duties on select brands.",
          img: "https://cdn.faire.com/fastly/ea018885012f63e650ef25e786b520c11235f42f319099218f7d8cd5cdc1ac35.png",
        }}
        values={{
          nbsp: NonBreaking.space,
        }}
      />
    );
  }

  return <>Insider</>;
};

const StyledTypography = styled(Typography).attrs({
  color: Decorative.shade.teal,
})`
  white-space: nowrap;
`;

const TextWrapper = styled.span`
  white-space: break-spaces;
`;

const InlineInsider = styled(Insider)`
  position: relative;
  top: 4px;
  vertical-align: baseline;
`;
